/* ============================================================================
   Morgan's Corner — live photo preview for cut-out ornaments
   ----------------------------------------------------------------------------
   Drop a transparent-window PNG in front of a customer-uploaded photo, let them
   drag / pinch / zoom to frame it, and send both the original file and the
   framing they approved along with the order.

   No dependencies. No app. Works on desktop, iOS and Android.

   Usage:
     <div data-photo-preview
          data-overlay="https://cdn.shopify.com/.../ornament-cutout.png"
          data-property="Upload"
          data-variant-match="custom,your own photo"
          data-attach-preview="true"></div>

   It self-starts on DOMContentLoaded. Call MCPhotoPreview.init() by hand only if
   you inject the markup later (a section re-render in the theme editor, say).
   ========================================================================== */
(function (global) {
  'use strict';

  var ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,.heic,.heif';
  var MAX_BYTES = 20 * 1024 * 1024; // Shopify's per-file ceiling on line items
  var MAX_STAGE = 1400;             // cap the working canvas so phones stay smooth

  /* ---------------------------------------------------------------- helpers */

  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'style') n.style.cssText = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (attrs[k] !== null && attrs[k] !== undefined) n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }

  function loadImage(src, cors) {
    return new Promise(function (res, rej) {
      var i = new Image();
      if (cors) i.crossOrigin = 'anonymous';
      i.onload = function () { res(i); };
      i.onerror = function () { rej(new Error('Could not load ' + src)); };
      i.src = src;
    });
  }

  /* Decode an uploaded file, honouring the phone's EXIF rotation. Returns null
     if the browser can't decode it (HEIC on anything that isn't Safari). */
  function decodeFile(file) {
    if (global.createImageBitmap) {
      return createImageBitmap(file, { imageOrientation: 'from-image' })
        .catch(function () { return decodeViaObjectURL(file); });
    }
    return decodeViaObjectURL(file);
  }

  function decodeViaObjectURL(file) {
    return new Promise(function (res) {
      var url = URL.createObjectURL(file);
      var i = new Image();
      i.onload = function () { res(i); };
      i.onerror = function () { URL.revokeObjectURL(url); res(null); };
      i.src = url;
    });
  }

  /* --------------------------------------------------- window auto-detection */
  /* Find the photo aperture in the overlay: the biggest island of transparent
     pixels that does NOT touch the outside edge. The hanger hole is transparent
     too, which is exactly why we take the largest island and not just any hole. */
  function detectWindow(img) {
    var SW = 220;
    var SH = Math.max(1, Math.round(img.naturalHeight * SW / img.naturalWidth));
    var c = document.createElement('canvas');
    c.width = SW; c.height = SH;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, SW, SH);
    ctx.drawImage(img, 0, 0, SW, SH);

    var data;
    try { data = ctx.getImageData(0, 0, SW, SH).data; }
    catch (e) { return null; }          // cross-origin without CORS headers

    var n = SW * SH;
    var clear = new Uint8Array(n);      // 1 = see-through
    var anyClear = false;
    for (var i = 0; i < n; i++) {
      if (data[i * 4 + 3] < 24) { clear[i] = 1; anyClear = true; }
    }
    if (!anyClear) return null;         // flat PNG, no cut-out

    // flood the outside so we can tell "around the ornament" from "inside it"
    var outside = new Uint8Array(n);
    var stack = [];
    for (var x = 0; x < SW; x++) { stack.push(x, x + (SH - 1) * SW); }
    for (var y = 0; y < SH; y++) { stack.push(y * SW, SW - 1 + y * SW); }
    while (stack.length) {
      var p = stack.pop();
      if (outside[p] || !clear[p]) continue;
      outside[p] = 1;
      var px = p % SW, py = (p / SW) | 0;
      if (px > 0) stack.push(p - 1);
      if (px < SW - 1) stack.push(p + 1);
      if (py > 0) stack.push(p - SW);
      if (py < SH - 1) stack.push(p + SW);
    }

    // label every interior hole
    var seen = new Uint8Array(n);
    var holes = [];
    for (var s = 0; s < n; s++) {
      if (!clear[s] || outside[s] || seen[s]) continue;
      var q = [s], area = 0, mnX = SW, mnY = SH, mxX = -1, mxY = -1;
      seen[s] = 1;
      while (q.length) {
        var k = q.pop(); area++;
        var kx = k % SW, ky = (k / SW) | 0;
        if (kx < mnX) mnX = kx; if (kx > mxX) mxX = kx;
        if (ky < mnY) mnY = ky; if (ky > mxY) mxY = ky;
        var nb = [kx > 0 ? k - 1 : -1, kx < SW - 1 ? k + 1 : -1,
                  ky > 0 ? k - SW : -1, ky < SH - 1 ? k + SW : -1];
        for (var b = 0; b < 4; b++) {
          var t = nb[b];
          if (t >= 0 && clear[t] && !outside[t] && !seen[t]) { seen[t] = 1; q.push(t); }
        }
      }
      if (area > n * 0.0006) {
        holes.push({ area: area, x0: mnX, y0: mnY, x1: mxX, y1: mxY });
      }
    }
    if (!holes.length) return null;

    holes.sort(function (a, b) { return b.area - a.area; });
    if (holes[0].area < n * 0.005) return null;        // nothing worth calling a window

    /* Real ornament art often splits the photo window into several holes — a script
       name, a bar, a divider laid across it. Those pieces are all one window and the
       photo has to cover all of them. So: start from the biggest hole, then absorb any
       other hole that is a meaningful size AND sits alongside it. A hanger hole is
       small and sits off on its own, so it never gets absorbed. Loop until nothing
       new joins, because absorbing one piece can bring another within reach. */
    var pad = Math.max(2, Math.round(Math.min(SW, SH) * 0.02));
    var floor = holes[0].area * 0.03;
    var win = { x0: holes[0].x0, y0: holes[0].y0, x1: holes[0].x1, y1: holes[0].y1 };
    var used = [true];
    for (var h = 1; h < holes.length; h++) used.push(false);
    var grew = true;
    while (grew) {
      grew = false;
      for (var j = 1; j < holes.length; j++) {
        if (used[j] || holes[j].area < floor) continue;
        var o = holes[j];
        if (o.x0 > win.x1 + pad || o.x1 < win.x0 - pad ||
            o.y0 > win.y1 + pad || o.y1 < win.y0 - pad) continue;
        if (o.x0 < win.x0) win.x0 = o.x0;
        if (o.y0 < win.y0) win.y0 = o.y0;
        if (o.x1 > win.x1) win.x1 = o.x1;
        if (o.y1 > win.y1) win.y1 = o.y1;
        used[j] = true;
        grew = true;
      }
    }

    return {                              // normalised 0..1 so it survives resizing
      x: win.x0 / SW, y: win.y0 / SH,
      w: (win.x1 - win.x0 + 1) / SW, h: (win.y1 - win.y0 + 1) / SH
    };
  }

  /* ------------------------------------------------------------------ widget */

  function Preview(root) {
    this.root = root;
    this.overlayUrl = root.getAttribute('data-overlay') || '';
    this.property = root.getAttribute('data-property') || 'Upload';
    this.positionProperty = root.getAttribute('data-position-property') || 'Photo Position';
    this.previewProperty = root.getAttribute('data-preview-property') || 'Approved Preview';
    this.attachPreview = root.getAttribute('data-attach-preview') !== 'false';
    this.required = root.getAttribute('data-required') !== 'false';
    this.matches = (root.getAttribute('data-variant-match') || '')
      .split(',').map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);

    /* A hand-supplied window ("x,y,w,h" as fractions of the picture), used only
       if we can't read the art at run time. Reading a picture back off a canvas
       is only allowed when the server that served it says so, and if that ever
       stops being true we still want the cut-out in the right place instead of
       a guess. */
    this.winFallback = null;
    var wf = (root.getAttribute('data-window') || '').split(',');
    if (wf.length === 4) {
      var nums = wf.map(function (v) { return parseFloat(v); });
      if (nums.every(function (v) { return isFinite(v); })) {
        this.winFallback = { x: nums[0], y: nums[1], w: nums[2], h: nums[3] };
      }
    }

    this.active = false;
    this.state = { cx: 0, cy: 0, scale: 1, rot: 0 };
    this.photo = null;
    this.overlay = null;
    this.win = null;
    this.pointers = new Map();
    this.build();
    this.start();
  }

  Preview.prototype.build = function () {
    var self = this;
    var uid = 'mcpv-' + Math.random().toString(36).slice(2, 8);

    this.stage = el('div', { class: 'mcpv__stage' });
    this.canvas = el('canvas', { class: 'mcpv__canvas', 'aria-label': 'Live preview of your ornament' });
    /* The prompt sits in a solid chip low in the picture rather than floating
       across the middle of it — the middle is where the ornament's own artwork
       is, and grey text laid over a script name is hard to read. */
    this.empty = el('div', { class: 'mcpv__empty' });
    var emptyBox = el('div', { class: 'mcpv__emptybox' });
    emptyBox.appendChild(el('span', { class: 'mcpv__emptyicon', html: '&#128247;' }));
    emptyBox.appendChild(el('span', { text: 'Add a photo to see it in the ornament' }));
    this.empty.appendChild(emptyBox);
    this.stage.appendChild(this.canvas);
    this.stage.appendChild(this.empty);

    this.fileInput = el('input', {
      type: 'file', accept: ACCEPT, id: uid + '-file',
      name: 'properties[' + this.property + ']', class: 'mcpv__file'
    });
    this.chooseBtn = el('label', {
      class: 'mcpv__btn mcpv__btn--primary', for: uid + '-file', text: 'Choose your photo'
    });

    this.zoom = el('input', { type: 'range', min: '100', max: '400', value: '100', class: 'mcpv__range', 'aria-label': 'Zoom' });
    this.rotate = el('input', { type: 'range', min: '-180', max: '180', value: '0', class: 'mcpv__range', 'aria-label': 'Rotate' });

    this.zoomRow = el('div', { class: 'mcpv__row' }, [
      el('span', { class: 'mcpv__rowlabel', text: 'Zoom' }), this.zoom
    ]);
    this.rotRow = el('div', { class: 'mcpv__row' }, [
      el('span', { class: 'mcpv__rowlabel', text: 'Rotate' }), this.rotate
    ]);

    this.resetBtn = el('button', { type: 'button', class: 'mcpv__btn', text: 'Re-centre' });
    this.clearBtn = el('button', { type: 'button', class: 'mcpv__btn', text: 'Remove photo' });

    this.tools = el('div', { class: 'mcpv__tools' }, [
      this.zoomRow, this.rotRow,
      el('div', { class: 'mcpv__btns' }, [this.resetBtn, this.clearBtn])
    ]);
    this.tools.style.display = 'none';

    this.hint = el('p', {
      class: 'mcpv__hint',
      text: 'Drag the photo to move it. Pinch or use the zoom slider to size it. Whatever shows through the cut-out is what gets made.'
    });
    /* Nothing to drag yet, so don't explain dragging yet. */
    this.hint.style.display = 'none';
    this.note = el('p', { class: 'mcpv__note' });
    this.note.style.display = 'none';

    this.positionInput = el('input', { type: 'hidden', name: 'properties[' + this.positionProperty + ']', value: '' });
    this.positionInput.disabled = true;

    this.previewInput = null;
    if (this.attachPreview && global.DataTransfer) {
      this.previewInput = el('input', {
        type: 'file', class: 'mcpv__file', tabindex: '-1',
        name: 'properties[' + this.previewProperty + ']'
      });
      this.previewInput.disabled = true;
    }

    this.wrap = el('div', { class: 'mcpv' }, [
      this.stage,
      el('div', { class: 'mcpv__actions' }, [this.chooseBtn]),
      this.tools, this.hint, this.note,
      this.fileInput, this.positionInput, this.previewInput
    ]);
    this.root.appendChild(this.wrap);

    this.fileInput.addEventListener('change', function () { self.onFile(); });
    this.resetBtn.addEventListener('click', function () { self.fit(); self.draw(); });
    this.clearBtn.addEventListener('click', function () { self.clearPhoto(); });
    this.zoom.addEventListener('input', function () { self.onZoom(); });
    this.rotate.addEventListener('input', function () { self.onRotate(); });
    this.bindDrag();
  };

  Preview.prototype.start = function () {
    var self = this;
    if (!this.overlayUrl) { this.wrap.style.display = 'none'; return; }
    loadImage(this.overlayUrl, true)
      .catch(function () { return loadImage(self.overlayUrl, false); })
      .then(function (img) {
        self.overlay = img;
        self.win = detectWindow(img) || self.winFallback || { x: 0.12, y: 0.12, w: 0.76, h: 0.76 };
        self.sizeStage();
        self.draw();
        self.watchVariant();
        global.addEventListener('resize', function () { self.sizeStage(); self.draw(); });
        /* Only now — with the art loaded and a preview actually on the page — do
           we let the theme hide the old uploader. If anything above went wrong we
           want the old one left alone and working. */
        if (document.body) document.body.classList.add('mcpv-live');
      })
      .catch(function () { self.wrap.style.display = 'none'; });
  };

  Preview.prototype.sizeStage = function () {
    var ratio = this.overlay.naturalHeight / this.overlay.naturalWidth;
    var cssW = this.stage.clientWidth || this.root.clientWidth || 320;
    var dpr = Math.min(global.devicePixelRatio || 1, 2);
    var W = Math.min(Math.round(cssW * dpr), MAX_STAGE);
    var H = Math.round(W * ratio);
    var had = this.canvas.width;
    this.canvas.width = W; this.canvas.height = H;
    this.canvas.style.width = '100%';
    this.canvas.style.height = 'auto';
    this.stage.style.aspectRatio = this.overlay.naturalWidth + ' / ' + this.overlay.naturalHeight;
    if (this.photo) {
      if (had) {                       // keep the framing when the stage resizes
        var k = W / had;
        this.state.cx *= k; this.state.cy *= k; this.state.scale *= k;
      } else { this.fit(); }
    }
  };

  Preview.prototype.winRect = function () {
    return {
      x: this.win.x * this.canvas.width,
      y: this.win.y * this.canvas.height,
      w: this.win.w * this.canvas.width,
      h: this.win.h * this.canvas.height
    };
  };

  /* smallest scale that still fills the window at the current rotation */
  Preview.prototype.minScale = function () {
    var r = this.winRect();
    var c = Math.cos(this.state.rot), s = Math.sin(this.state.rot);
    var hw = r.w / 2, hh = r.h / 2;
    var aw = Math.abs(hw * c) + Math.abs(hh * s);
    var ah = Math.abs(hw * s) + Math.abs(hh * c);
    return Math.max((aw * 2) / this.pw(), (ah * 2) / this.ph());
  };

  Preview.prototype.pw = function () { return this.photo.width || this.photo.naturalWidth; };
  Preview.prototype.ph = function () { return this.photo.height || this.photo.naturalHeight; };

  Preview.prototype.fit = function () {
    var r = this.winRect();
    this.state.rot = 0;
    this.rotate.value = 0;
    this.state.scale = this.minScale();
    this.state.cx = r.x + r.w / 2;
    this.state.cy = r.y + r.h / 2;
    this.zoom.value = 100;
    this.clamp();
  };

  /* never let the customer drag a bare corner into the window */
  Preview.prototype.clamp = function () {
    var min = this.minScale();
    if (this.state.scale < min) this.state.scale = min;

    var r = this.winRect();
    var c = Math.cos(this.state.rot), s = Math.sin(this.state.rot);
    var S = this.state.scale;
    var corners = [[r.x, r.y], [r.x + r.w, r.y], [r.x + r.w, r.y + r.h], [r.x, r.y + r.h]];
    var mnX = Infinity, mxX = -Infinity, mnY = Infinity, mxY = -Infinity;
    for (var i = 0; i < 4; i++) {
      var dx = corners[i][0] - this.state.cx, dy = corners[i][1] - this.state.cy;
      var lx = (dx * c + dy * s) / S, ly = (-dx * s + dy * c) / S;
      if (lx < mnX) mnX = lx; if (lx > mxX) mxX = lx;
      if (ly < mnY) mnY = ly; if (ly > mxY) mxY = ly;
    }
    var hw = this.pw() / 2, hh = this.ph() / 2, ox = 0, oy = 0;
    if (mnX < -hw) ox = -hw - mnX; else if (mxX > hw) ox = hw - mxX;
    if (mnY < -hh) oy = -hh - mnY; else if (mxY > hh) oy = hh - mxY;
    if (ox || oy) {
      this.state.cx -= (ox * c - oy * s) * S;
      this.state.cy -= (ox * s + oy * c) * S;
    }
  };

  Preview.prototype.draw = function () {
    if (!this.overlay) return;
    var ctx = this.canvas.getContext('2d');
    var W = this.canvas.width, H = this.canvas.height;
    ctx.clearRect(0, 0, W, H);
    if (this.photo) {
      ctx.save();
      ctx.translate(this.state.cx, this.state.cy);
      ctx.rotate(this.state.rot);
      var w = this.pw() * this.state.scale, h = this.ph() * this.state.scale;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.photo, -w / 2, -h / 2, w, h);
      ctx.restore();
    }
    ctx.drawImage(this.overlay, 0, 0, W, H);
    this.empty.style.display = this.photo ? 'none' : '';
    this.syncPosition();
  };

  Preview.prototype.onZoom = function () {
    if (!this.photo) return;
    var r = this.winRect();
    var pivotX = r.x + r.w / 2, pivotY = r.y + r.h / 2;
    var before = this.state.scale;
    var target = this.minScale() * (parseFloat(this.zoom.value) / 100);
    this.zoomAbout(pivotX, pivotY, target / before);
    this.clamp(); this.draw();
  };

  Preview.prototype.onRotate = function () {
    if (!this.photo) return;
    var r = this.winRect();
    var pivotX = r.x + r.w / 2, pivotY = r.y + r.h / 2;
    var next = parseFloat(this.rotate.value) * Math.PI / 180;
    var d = next - this.state.rot;
    var dx = this.state.cx - pivotX, dy = this.state.cy - pivotY;
    var c = Math.cos(d), s = Math.sin(d);
    this.state.cx = pivotX + dx * c - dy * s;
    this.state.cy = pivotY + dx * s + dy * c;
    this.state.rot = next;
    this.clamp(); this.draw();
  };

  Preview.prototype.zoomAbout = function (px, py, k) {
    this.state.cx = px + (this.state.cx - px) * k;
    this.state.cy = py + (this.state.cy - py) * k;
    this.state.scale *= k;
  };

  Preview.prototype.bindDrag = function () {
    var self = this;
    var last = null, lastDist = 0, lastMid = null;

    function pos(e) {
      var b = self.canvas.getBoundingClientRect();
      var k = self.canvas.width / b.width;
      return { x: (e.clientX - b.left) * k, y: (e.clientY - b.top) * k };
    }

    this.canvas.addEventListener('pointerdown', function (e) {
      if (!self.photo) return;
      self.canvas.setPointerCapture(e.pointerId);
      self.pointers.set(e.pointerId, pos(e));
      last = pos(e); lastDist = 0; lastMid = null;
      e.preventDefault();
    });

    this.canvas.addEventListener('pointermove', function (e) {
      if (!self.photo || !self.pointers.has(e.pointerId)) return;
      self.pointers.set(e.pointerId, pos(e));
      var pts = Array.from(self.pointers.values());

      if (pts.length >= 2) {
        var dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
        var dist = Math.hypot(dx, dy);
        var mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
        if (lastDist && lastMid) {
          self.zoomAbout(mid.x, mid.y, dist / lastDist);
          self.state.cx += mid.x - lastMid.x;
          self.state.cy += mid.y - lastMid.y;
          self.clamp(); self.draw(); self.syncZoomSlider();
        }
        lastDist = dist; lastMid = mid;
      } else {
        var p = pos(e);
        if (last) {
          self.state.cx += p.x - last.x;
          self.state.cy += p.y - last.y;
          self.clamp(); self.draw();
        }
        last = p;
      }
      e.preventDefault();
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      self.canvas.addEventListener(ev, function (e) {
        self.pointers.delete(e.pointerId);
        if (self.pointers.size < 2) { lastDist = 0; lastMid = null; }
        if (self.pointers.size === 0) last = null;
      });
    });

    this.canvas.addEventListener('wheel', function (e) {
      if (!self.photo) return;
      e.preventDefault();
      var p = pos(e);
      self.zoomAbout(p.x, p.y, e.deltaY < 0 ? 1.08 : 1 / 1.08);
      self.clamp(); self.draw(); self.syncZoomSlider();
    }, { passive: false });
  };

  Preview.prototype.syncZoomSlider = function () {
    var pct = Math.round(this.state.scale / this.minScale() * 100);
    this.zoom.value = Math.max(100, Math.min(400, pct));
  };

  Preview.prototype.onFile = function () {
    var self = this;
    var file = this.fileInput.files && this.fileInput.files[0];
    this.setNote('');
    if (!file) { this.clearPhoto(true); return; }

    if (file.size > MAX_BYTES) {
      this.setNote('That file is ' + (file.size / 1048576).toFixed(1) + ' MB. The most we can accept is 20 MB — please pick a smaller version.', true);
      this.fileInput.value = '';
      return;
    }

    decodeFile(file).then(function (img) {
      if (!img) {
        // HEIC from an iPhone, or something the browser simply can't open
        self.photo = null;
        self.tools.style.display = 'none';
        self.draw();
        self.setNote('We have your file and it came through fine — this browser just can’t open ' +
          'that format to show you a preview (iPhone HEIC photos do this). You can still add to cart, ' +
          'and we’ll frame it for you. To see the preview, set your iPhone camera to ' +
          'Settings → Camera → Formats → Most Compatible and take or re-save the photo.');
        return;
      }
      self.photo = img;
      self.tools.style.display = '';
      self.hint.style.display = '';
      self.chooseBtn.textContent = 'Change photo';
      self.sizeStage();
      self.fit();
      self.draw();
      self.root.dispatchEvent(new CustomEvent('photo-preview:change', { bubbles: true }));
    });
  };

  Preview.prototype.clearPhoto = function (keepFile) {
    this.photo = null;
    if (!keepFile) this.fileInput.value = '';
    this.tools.style.display = 'none';
    this.hint.style.display = 'none';
    this.chooseBtn.textContent = 'Choose your photo';
    this.setNote('');
    this.positionInput.disabled = true;
    if (this.previewInput) { this.previewInput.disabled = true; this.previewInput.value = ''; }
    this.draw();
  };

  Preview.prototype.setNote = function (msg, isError) {
    this.note.textContent = msg || '';
    this.note.style.display = msg ? '' : 'none';
    this.note.className = 'mcpv__note' + (isError ? ' mcpv__note--error' : '');
  };

  /* Write the framing back onto the form so the order carries it. */
  Preview.prototype.syncPosition = function () {
    if (!this.photo) return;
    var r = this.winRect();
    var relX = (this.state.cx - (r.x + r.w / 2)) / r.w;
    var relY = (this.state.cy - (r.y + r.h / 2)) / r.h;
    var zoom = this.state.scale / this.minScale();
    this.positionInput.disabled = false;
    this.positionInput.value =
      'zoom ' + zoom.toFixed(3) +
      ' | offset ' + relX.toFixed(3) + ',' + relY.toFixed(3) +
      ' | rotate ' + Math.round(this.state.rot * 180 / Math.PI) + '°' +
      ' | source ' + this.pw() + 'x' + this.ph();
  };

  /* Build a PNG of exactly what the customer sees and hand it to the form. */
  Preview.prototype.attachComposite = function () {
    if (!this.previewInput || !this.photo) return Promise.resolve();
    var self = this;
    var W = Math.min(this.overlay.naturalWidth, 1600);
    var H = Math.round(W * this.overlay.naturalHeight / this.overlay.naturalWidth);
    var k = W / this.canvas.width;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d');
    // White underneath so this can go out as a JPEG. A photographic mockup composites
    // to a megabyte as a PNG, and that megabyte gets uploaded on every add-to-cart,
    // over whatever phone signal the customer happens to have. JPEG is ~120 KB.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.translate(this.state.cx * k, this.state.cy * k);
    ctx.rotate(this.state.rot);
    var w = this.pw() * this.state.scale * k, h = this.ph() * this.state.scale * k;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.photo, -w / 2, -h / 2, w, h);
    ctx.restore();
    ctx.drawImage(this.overlay, 0, 0, W, H);

    /* Everything below is best-effort. This runs in the middle of an add-to-cart,
       so it must finish one way or another — if building the picture fails we
       let the order go through with the customer's file and their framing and
       simply skip the composite. An order that can't be placed is far worse than
       an order without a thumbnail. */
    return new Promise(function (res) {
      var settled = false;
      function finish() { if (!settled) { settled = true; res(); } }

      try {
        c.toBlob(function (blob) {
          if (!blob) return finish();
          try {
            var dt = new DataTransfer();
            dt.items.add(new File([blob], 'approved-preview.jpg', { type: 'image/jpeg' }));
            self.previewInput.files = dt.files;
            self.previewInput.disabled = false;
          } catch (e) { /* older browser — the original photo still goes through */ }
          finish();
        }, 'image/jpeg', 0.9);
      } catch (e) {
        /* The art was served without permission to read it back, which locks the
           canvas. Nothing we can do here, and nothing the customer needs to know. */
        finish();
      }

      setTimeout(finish, 8000);   // never let a stuck encoder hold an order hostage
    });
  };

  /* ------------------------------------------------- variant show/hide logic */

  Preview.prototype.watchVariant = function () {
    var self = this;
    if (!this.matches.length) { this.setActive(true); return; }
    var form = this.root.closest('form[action*="/cart/add"]');
    if (!form) { this.setActive(true); return; }
    var field = form.querySelector('[name="id"]');
    var apply = function () {
      var label = self.currentVariantLabel(form, field);
      var on = self.matches.some(function (m) { return label.indexOf(m) !== -1; });
      self.setActive(on);
    };
    apply();
    form.addEventListener('change', apply);
    document.addEventListener('variantChange', apply);
    if (field) {
      var seen = field.value;
      setInterval(function () { if (field.value !== seen) { seen = field.value; apply(); } }, 300);
    }
  };

  Preview.prototype.currentVariantLabel = function (form, field) {
    var bits = [];
    if (field && field.tagName === 'SELECT' && field.selectedOptions[0]) {
      bits.push(field.selectedOptions[0].textContent);
    }
    if (field && field.value && global.__mcpvVariantTitles && global.__mcpvVariantTitles[field.value]) {
      bits.push(global.__mcpvVariantTitles[field.value]);
    }
    form.querySelectorAll('input[type=radio]:checked, select[data-index], select[data-option]').forEach(function (n) {
      if (n.name === 'id') return;
      if (n.tagName === 'SELECT') { if (n.selectedOptions[0]) bits.push(n.selectedOptions[0].textContent); }
      else bits.push(n.value);
    });
    return bits.join(' ').toLowerCase();
  };

  /* Note there is deliberately no `required` attribute on the file input. It
     lives behind a styled button and is a pixel wide, and a browser asked to
     complain about a control it can't scroll into view gives up and cancels the
     submit with no message at all — the button would just look broken. We ask
     for the photo ourselves in the submit handler instead. */
  Preview.prototype.setActive = function (on) {
    this.active = !!on;
    this.wrap.style.display = on ? '' : 'none';
    if (!on) {
      this.fileInput.disabled = true;
      this.positionInput.disabled = true;
      if (this.previewInput) this.previewInput.disabled = true;
      return;
    }
    this.fileInput.disabled = false;
    /* The stage may have been off-screen when we first measured it, so re-check
       now that it's visible — otherwise the preview draws at the wrong size. */
    if (this.overlay) {
      var dpr = Math.min(global.devicePixelRatio || 1, 2);
      var want = Math.min(Math.round((this.stage.clientWidth || 0) * dpr), MAX_STAGE);
      if (want && Math.abs(want - this.canvas.width) > 2) this.sizeStage();
      this.draw();
    }
    this.syncPosition();
  };

  /* ------------------------------------------------------------------ styles */

  var CSS = [
    '.mcpv{margin:0 0 20px}',
    '.mcpv__stage{position:relative;width:100%;background:#f4f4f4;border:1px solid #e0e0e0;border-radius:10px;overflow:hidden;touch-action:none}',
    '.mcpv__canvas{display:block;width:100%;height:auto;cursor:grab}',
    '.mcpv__canvas:active{cursor:grabbing}',
    '.mcpv__empty{position:absolute;inset:0;display:flex;align-items:flex-end;justify-content:center;text-align:center;padding:0 12px 7%;pointer-events:none}',
    '.mcpv__emptybox{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.94);color:#2b2b2b;font-size:.88em;line-height:1.25;padding:9px 15px;border-radius:999px;box-shadow:0 2px 10px rgba(0,0,0,.18)}',
    '.mcpv__emptyicon{font-size:1.15em;line-height:1}',
    '.mcpv__file{position:absolute;width:1px;height:1px;opacity:0;overflow:hidden;clip:rect(0 0 0 0)}',
    '.mcpv__actions{margin-top:12px}',
    '.mcpv__btn{display:inline-block;border:1px solid #111;background:#fff;color:#111;border-radius:6px;padding:9px 16px;font:inherit;font-size:.9em;cursor:pointer;margin:0 8px 8px 0}',
    '.mcpv__btn--primary{background:#111;color:#fff}',
    '.mcpv__tools{margin-top:12px}',
    '.mcpv__row{display:flex;align-items:center;gap:10px;margin-bottom:8px}',
    '.mcpv__rowlabel{flex:0 0 58px;font-size:.85em;color:#555}',
    '.mcpv__range{flex:1 1 auto;width:100%}',
    '.mcpv__btns{margin-top:4px}',
    '.mcpv__hint{font-size:.85em;color:#555;margin:8px 0 0}',
    '.mcpv__note{font-size:.85em;margin:8px 0 0;padding:10px 12px;border-radius:6px;background:#fff6e5;border:1px solid #f0d9a8}',
    '.mcpv__note--error{background:#fdeaea;border-color:#f2b8b8}'
  ].join('');

  function injectCSS() {
    if (document.getElementById('mcpv-css')) return;
    var s = document.createElement('style');
    s.id = 'mcpv-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* --------------------------------------------------------------- bootstrap */

  var instances = [];

  function init(scope) {
    injectCSS();
    (scope || document).querySelectorAll('[data-photo-preview]').forEach(function (n) {
      if (n.__mcpv) return;
      n.__mcpv = new Preview(n);
      instances.push(n.__mcpv);
    });
    hookForms();
    return instances;
  }

  /* A file only reaches the order on a real form POST, and the composite has to
     be built before the browser serialises that form. Both are handled here.

     This listens on the document in the capture phase rather than on the form
     itself. Listeners attached to the same element run in the order they were
     added regardless of the capture flag, so binding to the form would leave us
     racing whatever the theme and the installed apps bound first. On the
     document we are guaranteed to go first. That matters because themes
     routinely turn add-to-cart into a background request so they can slide a
     cart drawer open, and a background request throws file uploads away. */
  var formsHooked = false;

  function hookForms() {
    if (formsHooked) return;
    formsHooked = true;

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

      var mine = instances.filter(function (p) {
        return p.active && form.contains(p.root) && p.wrap.style.display !== 'none';
      });
      if (!mine.length) return;                 // not our product, or not our variant

      form.setAttribute('enctype', 'multipart/form-data');

      var missing = mine.filter(function (p) {
        return p.required && !(p.fileInput.files && p.fileInput.files.length);
      });
      if (missing.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        missing[0].setNote('Please add your photo first — that’s the picture that goes in the ornament.', true);
        if (missing[0].wrap.scrollIntoView) {
          missing[0].wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }

      /* A file we couldn't open to draw (an iPhone HEIC, say) still has to reach
         the order — there's just no composite to build for it. */
      var drawable = mine.filter(function (p) { return p.photo; });
      if (!drawable.length) return;

      if (form.__mcpvGo) { form.__mcpvGo = false; return; }

      e.preventDefault();
      e.stopImmediatePropagation();

      Promise.all(drawable.map(function (p) { return p.attachComposite(); })).then(function () {
        if (form.getAttribute('data-mcpv-submit') === 'none') {
          form.dispatchEvent(new CustomEvent('mcpv:ready', { bubbles: true }));
          return;
        }
        form.__mcpvGo = true;
        HTMLFormElement.prototype.submit.call(form);   // fires no events; nothing can intercept it
      });
    }, true);
  }

  global.MCPhotoPreview = { init: init, instances: instances, detectWindow: detectWindow };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

})(window);
